import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import BingoResponseListItem from "./BingoResponseListItem";
import { useCallback, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { TypeFormResponse } from "@/types/bingo";
import { cn, wait } from "@/lib/utils";
import { Button } from "../ui/button";
import { ListRestart, EyeIcon, EyeOffIcon } from "lucide-react";
import { parseAsBoolean, useQueryState } from "nuqs";

const BingoResponseList = () => {
  const [isRefresing, setRefreshing] = useState(false);
  const [showHiddenResponses, setShowHiddenResponses] = useQueryState(
    "showHidden",
    parseAsBoolean.withOptions({ clearOnDefault: true }).withDefault(false)
  );

  const toggleShowingHiddenResponses = useCallback(() => {
    setShowHiddenResponses((show) => !show);
  }, [setShowHiddenResponses]);

  const {
    data: responses,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["bingo-response", showHiddenResponses ? "all-responses" : "active-responses"],
    queryFn: async () => {
      const queryBuilder = supabase
        .from("bingo_responses")
        .select("*")
        .order("submitted_at", { ascending: false });

      if (!showHiddenResponses) {
        queryBuilder.eq("is_hidden", false);
      }

      const { data, error } = await queryBuilder;
      if (error) throw error;
      return data;
    },
  });

  const updateBingoResponseStatus = useCallback(
    async (userId: string, formId: string, isDone: boolean, isHidden: boolean) => {
      try {
        await supabase
          .from("bingo_responses")
          .update({ is_done: isDone, is_hidden: isHidden })
          .eq("form_id", formId)
          .eq("user_id", userId);

        toast({
          title: "Success",
          description: "Updated bingo response",
        });

        // refetch the data
        refetch()

      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to update bingo response",
          variant: "destructive",
        });
      }
    },
    [refetch]
  );

  const mapResponsesToUsers = useCallback(async () => {
    setRefreshing(true);
    try {
      // call database funtion to map anonymous responses to user
      const builder = supabase.rpc("admin_map_bingo_responses_to_users");
      await wait(1000); // last for at least 1 second so the user can see the loading state
      const { data: numOfAffectRows, error } = await builder;

      if (error) {
        console.error("Error mapping responses to user:", error);
        toast({
          title: "Error",
          description: "Failed to map responses to user. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Mapping Success",
        description: `Mapped new ${numOfAffectRows} responses to users`,
      });

      refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  if (isLoading) {
    return <div>Loading responses...</div>;
  }

  return (
    <div className="pt-8 space-y-4">
      {/* action buttons */}
      <div className="w-full inline-flex items-center gap-4 lg:gap-8">
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={mapResponsesToUsers}
          disabled={isRefresing}
        >
          <ListRestart className="w-4 h-4" />
          {isRefresing ? "Mapping..." : "Map responses"}
        </Button>
        <Button
          variant="outline"
          className={cn(
            "flex items-center gap-2",
            showHiddenResponses ? "bg-muted" : "bg-success text-success-foreground hover:bg-success/90",
          )}
          onClick={toggleShowingHiddenResponses}
        >
          {showHiddenResponses && <EyeIcon className="w-4 h-4" />}
          {!showHiddenResponses && <EyeOffIcon className="w-4 h-4" />}
          Viewing: {showHiddenResponses ? "all items" : "active items"}
        </Button>
      </div>

      {/* response list */}
      <div className="w-full space-y-4">
        {responses?.map((response) => {
          return (
            <BingoResponseListItem
              key={response.id}
              formId={response.form_id}
              email={response.email}
              userId={response.user_id}
              isDone={response.is_done}
              isHidden={response.is_hidden}
              onUpdateStatus={updateBingoResponseStatus}
              response={response.form_response as TypeFormResponse}
            />
          );
        })}
      </div>
    </div>
  );
};

export default BingoResponseList;